import React, { useRef, useEffect } from 'react';

interface Testimonial {
  name: string;
  role: string;
  quote: string;
}

interface TestimonialCarouselProps {
  testimonials: Testimonial[];
}

export default function TestimonialCarousel({ testimonials }: TestimonialCarouselProps) {
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  let isDown = false;
  let startX: number;
  let scrollLeft: number;

  useEffect(() => {
    const scrollContainer = scrollContainerRef.current;
    if (!scrollContainer) return;

    const handleMouseDown = (e: MouseEvent) => {
      isDown = true;
      scrollContainer.classList.add('active');
      startX = e.pageX - scrollContainer.offsetLeft;
      scrollLeft = scrollContainer.scrollLeft;
    };

    const handleMouseLeave = () => {
      isDown = false;
      scrollContainer.classList.remove('active');
    };

    const handleMouseUp = () => {
      isDown = false;
      scrollContainer.classList.remove('active');
    };

    const handleMouseMove = (e: MouseEvent) => {
      if (!isDown) return;
      e.preventDefault();
      const x = e.pageX - scrollContainer.offsetLeft;
      const walk = (x - startX) * 2;
      scrollContainer.scrollLeft = scrollLeft - walk;
    };

    const handleTouchStart = (e: TouchEvent) => {
      isDown = true;
      startX = e.touches[0].pageX - scrollContainer.offsetLeft;
      scrollLeft = scrollContainer.scrollLeft;
    };

    const handleTouchMove = (e: TouchEvent) => {
      if (!isDown) return;
      const x = e.touches[0].pageX - scrollContainer.offsetLeft;
      const walk = (x - startX) * 2;
      scrollContainer.scrollLeft = scrollLeft - walk;
    };

    scrollContainer.addEventListener('mousedown', handleMouseDown);
    scrollContainer.addEventListener('mouseleave', handleMouseLeave);
    scrollContainer.addEventListener('mouseup', handleMouseUp);
    scrollContainer.addEventListener('mousemove', handleMouseMove);
    scrollContainer.addEventListener('touchstart', handleTouchStart);
    scrollContainer.addEventListener('touchmove', handleTouchMove);
    scrollContainer.addEventListener('touchend', handleMouseUp);

    return () => {
      scrollContainer.removeEventListener('mousedown', handleMouseDown);
      scrollContainer.removeEventListener('mouseleave', handleMouseLeave);
      scrollContainer.removeEventListener('mouseup', handleMouseUp);
      scrollContainer.removeEventListener('mousemove', handleMouseMove);
      scrollContainer.removeEventListener('touchstart', handleTouchStart);
      scrollContainer.removeEventListener('touchmove', handleTouchMove);
      scrollContainer.removeEventListener('touchend', handleMouseUp);
    };
  }, []);

  return (
    <div 
      ref={scrollContainerRef}
      className="flex overflow-x-auto snap-x snap-mandatory scrollbar-hide -mx-4 px-4 pb-6 scroll-smooth"
      style={{ scrollBehavior: 'smooth' }}
    >
      {testimonials.map((testimonial, index) => (
        <div 
          key={index}
          className="flex-none w-full sm:w-1/3 snap-center pr-4"
        >
          <blockquote className="bg-white rounded-xl shadow-lg hover:shadow-xl transition-shadow duration-200 p-6 h-full border border-brand-gray-100">
            <div className="flex items-center mb-4">
              <div className="w-12 h-12 rounded-full mr-4 bg-brand-blue-100 flex items-center justify-center text-brand-blue-600 text-xl font-bold shadow-inner">
                {testimonial.name[0]}
              </div>
              <div>
                <p className="font-bold text-brand-gray-900">{testimonial.name}</p>
                <p className="text-sm text-brand-gray-600">{testimonial.role}</p>
              </div>
            </div>
            <p className="text-lg text-brand-gray-600 mb-4 leading-relaxed">
              {testimonial.quote}
            </p>
          </blockquote>
        </div>
      ))}
    </div>
  );
}
